import { IncidentCard } from "@/components/IncidentCard";
import { TOP_N } from "@/lib/incidents";
import { Telescope, AlertTriangle, Globe2, Eye } from "lucide-react";
import { StormRainOutlook } from "@/components/StormRainOutlook";

const TAB = (on) => `flex items-center gap-1.5 h-11 px-2.5 text-[11px] font-display font-semibold tracking-widest uppercase border-b-2 transition-colors ${on ? "text-white border-atlas-orange" : "text-atlas-secondary border-transparent hover:text-white"}`;

const LedgerHeader = ({ tab, setTab, mainCount, monCount, total, readings, country, countries = [], onCountryChange }) => (
  <div className="h-11 shrink-0 flex items-center justify-between pr-3 border-b border-white/10 bg-atlas-surface">
    <div className="flex items-center">
      <button className={TAB(tab === "main")} onClick={() => setTab("main")} data-testid="ledger-tab-incidents">
        <Telescope className="w-3.5 h-3.5 text-atlas-orange" strokeWidth={1.8} /> Incidents <span className="font-mono text-[10px] text-atlas-secondary">{mainCount}</span>
      </button>
      <button className={TAB(tab === "monitoring")} onClick={() => setTab("monitoring")} data-testid="ledger-tab-monitoring"
        title="Analyst layer: confidence ≥ 70% but ≤ 10k people and no critical infrastructure at risk">
        <Eye className="w-3.5 h-3.5 text-atlas-secondary" strokeWidth={1.8} /> Monitoring <span className="font-mono text-[10px] text-atlas-secondary">{monCount}</span>
      </button>
    </div>
    <div className="flex items-center gap-2">
      <div className="relative flex items-center">
        <Globe2 className="w-3 h-3 text-atlas-secondary absolute left-2 pointer-events-none" strokeWidth={1.8} />
        <select
          data-testid="country-filter"
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
          className="appearance-none w-44 truncate bg-atlas-void border border-white/15 text-[10px] font-mono uppercase tracking-wider text-atlas-foreground pl-6 pr-6 h-6 hover:border-atlas-cyan/60 focus:border-atlas-cyan focus:outline-none cursor-pointer transition-colors"
        >
          <option value="all">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="absolute right-2 pointer-events-none text-atlas-secondary text-[8px]">▼</span>
      </div>
      <span
        className={`flex items-center gap-1 h-6 px-2 border text-[10px] font-mono ${total ? "border-atlas-red/40 text-atlas-red" : "border-white/15 text-atlas-secondary"}`}
        data-testid="ledger-anomaly-count"
        title={`${total} incidents grouped from ${readings} flagged readings`}
      >
        <AlertTriangle className="w-3 h-3" />
        {total} incidents · {readings} readings
      </span>
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="relative flex-1 flex items-center justify-center scanlines">
    <span className="text-[11px] font-mono text-atlas-secondary/75 uppercase tracking-widest text-center px-4">
      {text}
    </span>
  </div>
);

export const DiscoveryLedger = ({
  incidents: mainIncidents = [],
  monitoring = [],
  expiredCount = 0,
  storms = [],
  tab = "main",
  setTab = () => {},
  showAll = false,
  onToggleShowAll = () => {},
  hypByObs = {},
  onSelectObservation,
  onFocusIncident = () => {},
  activeUid,
  country = "all",
  countries = [],
  onCountryChange = () => {},
}) => {
  const incidents = tab === "main" ? mainIncidents : monitoring;
  const readings = incidents.reduce((n, i) => n + i.members.length, 0);
  const shown = showAll ? incidents : incidents.slice(0, TOP_N);
  return (
    <div className="w-full flex flex-col h-full border-l border-white/10 bg-atlas-void" data-testid="discovery-ledger">
      <LedgerHeader tab={tab} setTab={setTab} mainCount={mainIncidents.length} monCount={monitoring.length}
        total={incidents.length} readings={readings} country={country} countries={countries} onCountryChange={onCountryChange} />
      {tab === "main" && <StormRainOutlook storms={storms} />}
      {tab === "monitoring" && (
        <div className="px-3 py-2 text-[10px] font-mono text-atlas-secondary border-b border-white/10 bg-black/30" data-testid="monitoring-note">
          Confidence ≥ 70% but ≤ 10k people and no critical infrastructure at risk. {expiredCount} stale anomalies closed after 96 h.
        </div>
      )}
      {incidents.length === 0 ? (
        <EmptyState text={tab === "monitoring" ? "Nothing in the monitoring layer right now." : country !== "all" ? `No ${country} incidents for this hazard right now.` : "No incidents at ≥ 70% confidence with major exposure right now. The engine keeps scanning."} />
      ) : (
        <div className="flex-1 overflow-y-auto min-w-0">
          <div className="px-3 py-2 flex items-center justify-between border-b border-white/10 bg-black/20">
            <span className="text-[10px] font-mono uppercase tracking-widest text-atlas-secondary" data-testid="incidents-mode-label">
              {showAll ? `All ${incidents.length} incidents` : `Top ${shown.length} by urgency`}
            </span>
            {incidents.length > TOP_N && (
              <button onClick={onToggleShowAll} data-testid="incidents-show-all"
                className="text-[10px] font-mono uppercase tracking-wider text-atlas-cyan hover:text-white transition-colors">
                {showAll ? `Top ${TOP_N} only` : `Show all ${incidents.length}`}
              </button>
            )}
          </div>
          <div data-testid="observations-list">
            {shown.map((inc, i) => (
              <IncidentCard key={inc.id} inc={inc} rank={i + 1} hypByObs={hypByObs} onSelect={onSelectObservation}
                onFocus={onFocusIncident} activeUid={activeUid} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
